import { createPortal } from 'react-dom';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { CapabilityProvider } from './CapabilityProvider';
import AdminSidebar from './AdminSidebar';
import AdminErrorBoundary from './AdminErrorBoundary';
import NotificationBell from './NotificationBell';
import { useCapabilities } from './CapabilityProvider';
import { clearCapabilityCache } from '../../services/capabilityService';
import { adminLogout } from '../../services/adminAuthService';
import { spacing } from '../admin/ui/tokens';
import AuthDebugPanel from './AuthDebugPanel';
import Icon from './ui/icons/Icon';
import ThemeToggle from './ui/ThemeToggle';
import DensityToggle from './ui/DensityToggle';
import HeaderBrand from './ui/HeaderBrand';
import '../../styles/tokens/clc.css'; /* Design tokens - must load first */
import './AdminLayout.css';
import '../../styles/admin-layout.css';
import '../../styles/admin-ui.css'; /* Global Admin UI - Visual Identity */

function isUuidLike(value) {
  return typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

function getAdminDisplayName(me) {
  if (!me) return 'Admin';

  const email = typeof me.email === 'string' ? me.email.trim() : '';
  if (email && !isUuidLike(email)) {
    const [local] = email.split('@');
    return local || email;
  }

  const username = typeof me.username === 'string' ? me.username.trim() : '';
  if (username && !isUuidLike(username)) return username;

  const actorId = typeof me.actorId === 'string' ? me.actorId.trim() : '';
  if (actorId && !isUuidLike(actorId)) return actorId;

  return 'Admin';
}

function getInitials(label) {
  if (!label) return 'A';
  const parts = String(label)
    .replace(/[@._-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return 'A';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function AccountControls() {
  const { me } = useCapabilities();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  const logoutInFlight = useRef(false);
  
  useEffect(() => {
    // Close menu when clicking outside
    const handleClickOutside = (e) => {
      if (menuOpen && !e.target.closest('[data-account-controls]')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);
  
  const handleSignOut = async () => {
    if (logoutInFlight.current) return;
    logoutInFlight.current = true;
    setLogoutPending(true);
    setLogoutError(false);
    try {
      await adminLogout();
      clearCapabilityCache();
      window.location.href = '/admin/login';
    } catch {
      // The server may have acted even when its response was lost. Keep the
      // current view and describe uncertainty rather than claiming sign-out.
      setLogoutError(true);
    } finally {
      logoutInFlight.current = false;
      setLogoutPending(false);
    }
  };

  const handleSwitchAccount = handleSignOut;

  if (!me) {
    return null; // Don't show if not loaded yet
  }

  const displayName = getAdminDisplayName(me);
  const initials = getInitials(displayName);
  
  return (
    <div className="admin-account-controls" data-account-controls>
      {(logoutPending || logoutError) && createPortal(
        <section role={logoutError ? 'alert' : 'status'} aria-label="Sign-out status"
          style={{ position: 'fixed', top: 80, right: 16, zIndex: 10001, width: 'min(320px, calc(100vw - 32px))', boxSizing: 'border-box', padding: 16, borderRadius: 12, background: 'var(--color-bg-primary, white)', color: 'var(--color-text-primary, #172b3a)', border: '1px solid #94a3b8', boxShadow: '0 8px 24px #0003' }}>
          <p style={{ margin: '0 0 12px' }}>{logoutPending ? 'Signing out…' : 'Sign-out could not be confirmed. Your session may still be active.'}</p>
          {logoutError && <>
            <button type="button" className="admin-account-menu-item" onClick={handleSignOut}>Retry sign out</button>
            <button type="button" className="admin-account-menu-item" onClick={() => setLogoutError(false)}>Dismiss</button>
          </>}
        </section>, document.body
      )}
      <div className="admin-account-summary" aria-label={`Signed in as ${displayName}`}>
        <span className="admin-account-avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="admin-account-name">{displayName}</span>
      </div>
      <div className="admin-account-dropdown">
        <button
          className="admin-account-trigger"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          aria-label="Account menu"
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          <Icon name="settings" size="md" />
        </button>
        {menuOpen && (
          <div className="admin-account-menu" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handleSwitchAccount}
              disabled={logoutPending}
              className="admin-account-menu-item"
              aria-label="Switch account"
            >
              Switch Account
            </button>
            <button
              onClick={handleSignOut}
              disabled={logoutPending}
              className="admin-account-menu-item"
              aria-label="Sign out"
            >
              {logoutPending ? 'Signing out…' : 'Sign Out'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCompact, setSidebarCompact] = useState(false);

  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 1024px)').matches);
  const sidebarRef = useRef(null);
  const contentRef = useRef(null);
  const toggleRef = useRef(null);
  const location = useLocation();
  const drawerOpen = mobile && sidebarOpen;
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 1024px)');
    const update = () => {
      // Move focus before a visible desktop sidebar becomes hidden on resize.
      if (query.matches && sidebarRef.current?.contains(document.activeElement)) {
        toggleRef.current?.focus({ preventScroll: true });
      }
      setMobile(query.matches);
      setSidebarOpen(false);
    };
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(closeSidebar, [location.pathname, location.search, closeSidebar]);

  useLayoutEffect(() => {
    if (!drawerOpen) return;
    const sidebar = sidebarRef.current;
    const content = contentRef.current;
    const focusable = () => [...sidebar.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')]
      .filter(element => element.getClientRects().length > 0);
    const focusFirst = () => (focusable()[0] || sidebar).focus({ preventScroll: true });
    focusFirst();
    content.inert = true;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSidebar();
      } else if (event.key === 'Tab') {
        const items = focusable();
        const first = items[0];
        const last = items[items.length - 1];
        if (!first) { event.preventDefault(); sidebar.focus({ preventScroll: true }); }
        else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus({ preventScroll: true });
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus({ preventScroll: true });
        }
      }
    };
    const containFocus = event => {
      if (!sidebar.contains(event.target)) focusFirst();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('focusin', containFocus);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('focusin', containFocus);
      content.inert = false;
      document.body.style.overflow = previousOverflow;
      if (window.matchMedia('(max-width: 1024px)').matches) toggleRef.current?.focus({ preventScroll: true });
    };
  }, [drawerOpen, closeSidebar]);

  // Calculate sidebar width based on state
  const sidebarWidth = sidebarCompact ? 64 : 280; /* 280px - authoritative width */

  return (
    <CapabilityProvider>
      <style>{`
        /* Global focus styles for accessibility */
        button:focus-visible,
        a:focus-visible,
        input:focus-visible,
        select:focus-visible,
        textarea:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
        /* Skip link focus */
        a[href="#main-content"]:focus {
          top: 0;
        }
        /* Sidebar width CSS variable - single source of truth */
        .admin-layout-root {
          --admin-sidebar-width: ${sidebarWidth}px;
        }
      `}</style>
      <div style={styles.container} className="admin-layout-root">
        {/* Skip to main content link for keyboard users */}
        <a 
          href="#main-content" 
          style={styles.skipLink}
          onFocus={(e) => {
            e.currentTarget.style.top = '0';
          }}
          onBlur={(e) => {
            e.currentTarget.style.top = '-40px';
          }}
        >
          Skip to main content
        </a>
        <>
          <AdminSidebar 
            sidebarOpen={drawerOpen}
            mobile={mobile}
            sidebarRef={sidebarRef} 
            onClose={closeSidebar}
            compact={!mobile && sidebarCompact}
            onCompactChange={setSidebarCompact}
          />
          {drawerOpen && (
            <div 
              className="admin-sidebar-overlay"
              onClick={closeSidebar}
              aria-label="Close sidebar"
            />
          )}
        </>
        <div className="admin-content-wrapper" ref={contentRef}>
          <header className="admin-layout-header">
            <div className="admin-header-left">
              <button
                ref={toggleRef}
                aria-controls="admin-navigation"
                className="admin-mobile-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle navigation"
                aria-expanded={drawerOpen}
              >
                ☰
              </button>
              <HeaderBrand />
            </div>
            <div className="admin-header-right">
              <div className="admin-header-mode-group" aria-label="Display controls">
                <DensityToggle />
                <ThemeToggle />
              </div>
              <div className="admin-header-utility-cluster" aria-label="Account and notifications">
                <NotificationBell />
                <AccountControls />
              </div>
            </div>
          </header>
          <main id="main-content" style={styles.main} className="admin-main" role="main" aria-label="Admin panel main content">
            <AdminErrorBoundary>
              <Outlet />
            </AdminErrorBoundary>
          </main>
        </div>
      </div>
      <AuthDebugPanel />
      {/* Toast container will be added by toast context/provider when implemented */}
    </CapabilityProvider>
  );
}

const styles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: 'var(--color-bg-secondary)', /* Subtle app background */
  },
  // contentWrapper styles moved to CSS - uses --admin-sidebar-width variable
  skipLink: {
    position: 'absolute',
    top: '-40px',
    left: '0',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    padding: '0.5rem 1rem',
    textDecoration: 'none',
    zIndex: 10000,
    borderRadius: '0 0 var(--radius-sm) 0',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
  },
  skipLinkFocus: {
    top: '0',
  },
  main: {
    flex: 1,
    padding: 'var(--spacing-4) var(--spacing-6)', /* 16px top, 24px sides - optimized for above-fold */
    maxWidth: '1800px',
    width: '100%',
    margin: '0 auto',
    backgroundColor: 'transparent', /* Transparent to show app background for surface layering */
  },
};

// Responsive styles moved to admin-layout.css - uses CSS variables and proper breakpoints
